<script setup>
import { computed, defineProps } from 'vue';
import MsToolTip from '@/components/tooltip/MsToolTip.vue';
import MsImage from '@/components/image/MsImage.vue';
import avatarApi from '@/apis/other/avatarAPI';
import { MsPopoverPosition } from '@/lib/vueFinalPopover/VueFinalPopover.vue';

const props = defineProps({
  /**
   * Thông tin người dùng
   */
  userInfo: {
    type: Object,
    required: true,
  },

  /**
   * Vị trí tooltip
   */
  position: {
    type: Object,
    default: () => ({
      x: MsPopoverPosition.Left,
      y: MsPopoverPosition.Bottom,
      offsetX: 0,
      offsetY: 0,
      isOuterX: false,
      isOuterY: true,
    }),
  },
  /**
   * Có đóng khi hover vào popover ko
   */
  isCloseWhenHoverPopover: {
    type: Boolean,
    default: false,
  },
});

/**
 * Lấy vị trí và đơn vị người dùng
 * @returns vị trí và đơn vị của người dùng
 * @author: BMThang(06/12/2022)
 */
const computedOrg = computed(() => {
  const userInfo = props.userInfo;
  let result = '';
  if (userInfo.OrganizationUnitName && userInfo.OrganizationUnitID) {
    if (result) {
      result = `${result} - ${userInfo.OrganizationUnitName}`;
    } else {
      result = userInfo.OrganizationUnitName;
    }
  }
  if (
    userInfo.OrganizationName &&
    userInfo.OrganizationID &&
    userInfo.OrganizationUnitID !== userInfo.OrganizationID
  ) {
    if (result) {
      result = `${result} - ${userInfo.OrganizationName}`;
    } else {
      result = userInfo.OrganizationName;
    }
  }

  return result;
});

/**
 * Lấy avatar người dùng
 * @returns avatar người dùng
 * @author: BMThang(06/12/2022)
 */
const computedUserAvatar = computed(() => {
  const userInfo = props.userInfo;
  return avatarApi.getAvatarLink(userInfo.MISAID, 40);
});
</script>

<template>
  <ms-tool-tip 
    :position="position"
    :is-close-when-hover-popover="isCloseWhenHoverPopover"
  >
    <slot />
    <template #content>
      <div class="display-user-tooltip">
        <!-- Avatar -->
        <div class="display-user-tooltip-avatar">
          <div class="display-user-tooltip-avatar-wrapper">
            <MsImage :src="computedUserAvatar" :alt="userInfo.FullName" />
          </div>
        </div>

        <!-- Info -->
        <div class="display-user-tooltip-info">
          <div class="display-user-tooltip-name" :title="userInfo.FullName">
            {{ userInfo.FullName }}
          </div>
          <div
            v-if="userInfo.JobTitleName"
            class="display-user-tooltip-position"
            :title="userInfo.JobTitleName"
          >
            {{ userInfo.JobTitleName }}
          </div>
          <div class="display-user-tooltip-org" :title="computedOrg">
            {{ computedOrg }}
          </div>
        </div>
      </div>
    </template>
  </ms-tool-tip>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.display-user-tooltip {
  display: flex;
  align-items: flex-start;
  width: 312px;
  padding: 6px 4px;
  // Avatar
  .display-user-tooltip-avatar {
    width: 40px;
    height: 40px;
    margin-right: 8px;
    position: relative;
    .display-user-tooltip-avatar-wrapper {
      width: 100%;
      height: 100%;
      overflow: hidden;
      border-radius: 50%;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
        border-radius: 50%;
        overflow: hidden;
      }
    }
  }
  // Info
  .display-user-tooltip-info {
    width: calc(100% - 48px);
    div {
      width: 100%;
      &.display-user-tooltip-name {
        font-size: $font-size-body;
        font-weight: $font-weight-medium;
        margin-bottom: 4px;
      }
      &.display-user-tooltip-org,
      &.display-user-tooltip-position {
        margin-bottom: 4px;
        font-size: $font-size-caption;
        font-weight: $font-weight-regular;
        color: var(--black-10);
      }
      &.display-user-tooltip-org {
        margin-bottom: 2px;
      }
    }
  }
}
</style>